import { get, size } from "lodash"
import { getModelFactorsClassification } from "selectors/modelMetadata"
import { Creators as NotificationsCreators } from "modules/notifications/model"
import { generateClassificationQuery, classificationsQuery } from "helpers/classification"
import { getIsOpPro } from "modules/account-root/selectors"
import { getIsModelInQueryString } from "selectors/queryString"
import withUrlParams from "hocs/withUrlParams"
import { BASKETS_QUERY } from "resources/baskets"
import { MODEL_METADATA_QUERY } from "resources/models"
import { CONTENT_SETS_METADATA_QUERY } from "resources/content-sets"
import { FAVORITE_FACTORS_TYPE } from "modules/portfolio-root/model"
import { getAssetDetailsDir, isIndexAsset } from "./helpers"
import createModule from "../createModule"
import view from "./view"
import {
  getAssetId,
  getInternalId,
  getAssetDetails,
  getAssetType,
  getCurrentGrouping,
  getAvailableRollups,
  getExpand,
  getAssetTickerMic,
  getSelectedModelId,
  getSelectedModelName,
  getShowNotHeld,
  getAnalyzeMenuItems,
  getClassifications,
  getSelectedScreen,
  getSelectedSubScreen,
  getSubScreenId,
  getMenuItems,
  getSubMenuItems,
  getAssetName,
  getModelSupportsComposites,
  getCurrency,
  getUnsupportedCurrencyWarning,
  getFactorSensitivityType,
  getSelectedAnalyzeView,
  getNormalization,
  getDisplayShowNotHeld,
  getBasePositionSetWithSegment,
  getIsDownloadAllowed,
} from "./selectors"
import {
  getBaseDropdownVisible,
  getBaseSearchValue,
  getBaseSignature,
  getBaseStartDate,
  getFullBenchmark,
  getIsActive,
  getBaseSearchResults,
  getSelectedBase,
  getViewType,
  getResourceId,
  getBaseHistory,
} from "./selectors/base"
import {
  getSelectedDate,
  getStartDate,
  getEndDate,
  getEarliestDate,
  getLatestDate,
  getPeriodOrStartAndEndDates,
  getIsPeriodAdjusted,
  getIsSelectedDateAdjusted,
  getAssetDetailsDate,
  getDriftDates,
} from "./selectors/dates"
import { getDisplayAddView, getSelectedViews, getSelectedViewsFactors, getCategories, getAggregationFunction, getMustAggregateFactors, getFavoriteFactors, getFactorsMetadata, getSelectViewAggregatedFactorsMetadata } from "./selectors/factors"
import { getFilterOns, getFiltersVisible, getClassificationsTiers, getFilter, getSegment, getAddedFilters } from "./selectors/filters"
import { Creators } from "./model"
import { getPerformanceFetchStartDate } from "./selectors/performance"
import { getAdjustedNormalizationProperties } from "./selectors/normalization"
import { getRecentInstruments } from "selectors/recentInstruments"

export default withUrlParams(createModule({
  view,
  externalProps: (state, props) => ({
    account: get(state, "global.selected.account"),
    accountId: get(state, "global.selected.accountId"),
    startDate: getStartDate(state),
    endDate: getEndDate(state),
    modelId: getSelectedModelId(state),
    latestDate: getLatestDate(state),
    earliestDate: getEarliestDate(state),
    assetId: getAssetId(state, props),
    assetType: getAssetType(state, props),
    asset: getAssetDetails(state, props),
    classificationsTiers: getClassificationsTiers(state, props),
    expand: getExpand(state, props),
    segment: getSegment(state, props),
    assetDetailsDate: getAssetDetailsDate(state),
    recentInstruments: getRecentInstruments(state, props),
  }),
  required: ["account", "modelId"],
  data: [
    MODEL_METADATA_QUERY, {
      resource: "dates",
      url: ({ modelId }) => {
        if (!modelId) {
          return null
        }

        return {
          id: modelId,
          query: "query ($modelId: String!) { model (id: $modelId) { availability { currentDate, factorsStartDate, securitiesStartDate } } }",
          variables: { modelId },
        }
      },
    }, {
      resource: "modeledDates",
      url: ({ modelId, latestDate, earliestDate }) => {
        if (!modelId || !latestDate || !earliestDate) {
          return null
        }

        return {
          id: `${modelId}${latestDate}${earliestDate}`,
          query: "query ($modelId: String!, $from: Date!, $to: Date!) { model (id: $modelId) { availability { dates(from: $from, to: $to) } } }",
          variables: { modelId, from: earliestDate, to: latestDate },
        }
      },
    }, {
      resource: "assetDetails",
      url: ({ account, modelId, assetId, assetType, assetDetailsDate }) => {
        if (!assetDetailsDate) {
          return null
        }

        // We need to call this endpoint because the ID that is set in the URL could be anything (internal, sedol, isin, ticker & mic, etc.)
        const url = `/api/${account}/m/${modelId}/${getAssetDetailsDir(assetType)}/${assetId}/details`

        return isIndexAsset(assetType) ? url : `${url}?date=${assetDetailsDate}`
      },
    }, {
      resource: "resourceMeta",
      url: ({ account, modelId, assetType, assetId }) => {
        if (!modelId || assetType !== "basket") {
          return null
        }

        return `/api/${account}/m/${modelId}/r/${assetType}/${assetId}`
      },
    }, {
      resource: "assetMeta",
      url: ({ account, asset, modelId, endDate }) => {
        if (!modelId || !asset || asset.assetSubclass !== "etf" || !endDate) {
          return null
        }

        return `/api/${account}/m/${modelId}/etfs/${asset.modelProviderId}?date=${endDate}`
      },
    }, {
      resource: "securitiesDescriptors",
      url: ({ account, modelId, securitiesIds, endDate }) => {
        if (size(securitiesIds) === 0) {
          return null
        }

        return {
          url: `/api/${account}/m/${modelId}/securities/descriptors?date=${endDate}`,
          id: `securitiesDescriptors-${account}${modelId}${endDate}${JSON.stringify(securitiesIds)}`,
          payload: { ids: securitiesIds },
        }
      },
    }, {
      resource: "classifications",
      url: () => ({
        id: "classifications",
        query: classificationsQuery,
      }),
    }, {
      resource: "classificationsValues",
      as: null,
      url: ({ classificationsTiers, startDate, endDate }) => {
        // Make sure we also have an end date because otherwise the start date is calculated relative to today which makes
        // this query execute twice during first page load
        if (size(classificationsTiers) === 0 || !startDate || !endDate) {
          return null
        }

        // We don't know the depth of each classification, generate each classification sub query according to the tier depth
        const innerQuery = generateClassificationQuery(classificationsTiers)
        return {
          id: `classificationsValues-${startDate}-${JSON.stringify(classificationsTiers)}`,
          query: `query {
            ${innerQuery}
          }`,
        }
      },
    }, {
      resource: "assetFilters",
      url: ({ account, modelId, asset, startDate, endDate }) => {
        if (!asset || !endDate) {
          return null
        }

        const parameters = `startDate=${startDate}&endDate=${endDate}`

        if (asset.assetClass === "swap") {
          return `/api/${account}/m/${modelId}/r/basket/${asset.ticker}/filters?${parameters}`
        }

        if (asset.assetSubclass === "etf") {
          return `/api/${account}/m/${modelId}/etfs/${asset.modelProviderId}/filters?${parameters}`
        }

        if (asset.indexId) {
          return `/api/${account}/m/${modelId}/indices/${asset.indexId}/filters?${parameters}`
        }
      },
    }, {
      resource: "userFavoriteFactors",
      passthrough: true,
      url: ({ accountId, modelId }) => {
        if (!modelId || !accountId) {
          return null
        }

        return {
          id: `userFavoriteFactors-${accountId}${modelId}`,
          url: "/api/users/me/preferences",
          payload: {
            items: [{
              type: FAVORITE_FACTORS_TYPE,
              key: `${modelId}`,
              accountId,
            }],
          },
        }
      },
    }, {
      resource: "etfs",
      url: ({ account, modelId }) => (modelId ? `/api/${account}/m/${modelId}/etfs` : null),
    }, {
      resource: "benchmarksAndPortfolios",
      url: ({ modelId }) => {
        if (!modelId) {
          return null
        }

        return ({
          id: `benchmarksAndPortfolios-${modelId}`,
          query: `query ($modelId: String!) {
            model (id: $modelId) {
              benchmarks { id, name, availableFrom }
            }
            portfolios { id, availableFrom }
          }`,
          variables: { modelId },
        })
      },
    }, {
      resource: "indices",
      url: () => ({
        id: "indices",
        query: `query {
        indexes { id name availableFrom }
      }`,
      }),
    }, CONTENT_SETS_METADATA_QUERY, {
      resource: "compositeForecasts",
      url: () => ({
        id: "compositeForecasts",
        query: `query {
        forecasts {
          id
          name
        }
      }`,
      }),
    }, BASKETS_QUERY, {
      resource: "userDefaultMetricUnits",
      url: ({ accountId }) => {
        if (!accountId) {
          return null
        }

        return {
          id: `userDefaultMetricUnits-${accountId}`,
          url: "/api/users/me/preferences",
          payload: {
            items: [{
              type: "defaultMetricUnits",
              key: accountId,
              accountId,
            }],
          },
        }
      },
    }],
  mapStateToProps: (state, props) => ({
    categories: getCategories(state, props),
    internalId: getInternalId(state, props),
    resourceId: getResourceId(state, props),
    contentSetId: getSubScreenId(state, props),
    forecastId: getSubScreenId(state, props),
    endDate: getEndDate(state, props),
    selectedDate: getSelectedDate(state, props),
    driftDates: getDriftDates(state, props),
    period: getPeriodOrStartAndEndDates(state, props),
    periodAdjusted: getIsPeriodAdjusted(state, props),
    displayAddView: getDisplayAddView(state, props),
    selectedViews: getSelectedViews(state, props),
    selectedViewsFactors: getSelectedViewsFactors(state, props),
    favoriteFactors: getFavoriteFactors(state, props),
    factorsMetadata: getFactorsMetadata(state, props),
    classifications: getClassifications(state, props),
    selectedDateAdjusted: getIsSelectedDateAdjusted(state, props),
    mustAggregateFactors: getMustAggregateFactors(state, props),
    factorsClassification: getModelFactorsClassification(state, props),
    aggregationFunction: getAggregationFunction(state, props),
    aggregatedFactorsMetadata: getSelectViewAggregatedFactorsMetadata(state, props),
    filter: getFilter(state, props),
    filterOns: getFilterOns(state, props),
    filtersVisible: getFiltersVisible(state, props),
    availableRollups: getAvailableRollups(state, props),
    currentGrouping: getCurrentGrouping(state, props),
    expand: getExpand(state, props),
    assetTickerMic: getAssetTickerMic(state, props),
    assetName: getAssetName(state, props),
    segment: getSegment(state, props),
    normalization: getNormalization(state, props),
    selectedScreen: getSelectedScreen(state, props),
    selectedSubScreen: getSelectedSubScreen(state, props),
    selectedModelName: getSelectedModelName(state, props),
    isActive: getIsActive(state, props),
    benchmark: (state, props),
    selectedBase: getSelectedBase(state, props),
    baseSearchValue: getBaseSearchValue(state, props),
    baseDropdownVisible: getBaseDropdownVisible(state, props),
    defaultSelectedBase: getFullBenchmark(state, props),
    searchResults: getBaseSearchResults(state, props),
    viewType: getViewType(state, props),
    base: getBasePositionSetWithSegment(state, props),
    baseStartDate: getBaseStartDate(state, props),
    baseSignature: getBaseSignature(state, props),
    baseHistory: getBaseHistory(state, props),
    showNotHeld: getShowNotHeld(state, props),
    displayShowNotHeld: getDisplayShowNotHeld(state, props),
    isOpPro: getIsOpPro(state, props),
    analyzeMenuItems: getAnalyzeMenuItems(state, props),
    menuItems: getMenuItems(state, props),
    subMenuItems: getSubMenuItems(state, props),
    modelSupportsComposites: getModelSupportsComposites(state, props),
    performanceStartDate: getPerformanceFetchStartDate(state, props),
    currency: getCurrency(state, props),
    unsupportedCurrencyWarning: getUnsupportedCurrencyWarning(state, props),
    factorSensitivityType: getFactorSensitivityType(state, props),
    selectedAnalyzeView: getSelectedAnalyzeView(state, props),
    ...getAdjustedNormalizationProperties(state, props),
    isModelInQueryString: getIsModelInQueryString(state, props),
    addedFilters: getAddedFilters(state, props),
    isDownloadAllowed: getIsDownloadAllowed(state, props),
  }),
  actions: {
    ...Creators,
    addNotification: NotificationsCreators.add,
  },
}))
